import type { ChangeEvent, FocusEvent } from 'react';
import type { TypeaheadMatchFn } from '../../select/typeahead/hooks/use-typeahead-filtering';
import type { DropdownCloseStrategy, TypeaheadInteractionContext, TypeaheadValidationStrategy } from '../../select/typeahead/types';
import type { AddressFieldStatus } from '../types';
import { useDawaAddressAdapter, type UseDawaAddressAdapterParams, type UseDawaAddressAdapterResult } from './use-dawa-address-adapter';

type UseAddressSuggestionServiceParams = UseDawaAddressAdapterParams;

type UseAddressSuggestionServiceResult = {
    options: UseDawaAddressAdapterResult['options'];
    matchOption: TypeaheadMatchFn;
    isLoading: boolean;
    status: AddressFieldStatus;
    kvhx?: string;
    kvhxUrl?: string;
    selectedSuggestion?: UseDawaAddressAdapterResult['selectedSuggestion'];
    selectedCustomValue?: string;
    selectedOptionValue?: string;
    validationStrategy: TypeaheadValidationStrategy;
    dropdownCloseStrategy: DropdownCloseStrategy;
    shouldAutoOpen: (value: string) => boolean;
    shouldOpenOnFocus: boolean;
    handleInputValueChange: (value: string) => void;
    handleHiddenInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleFocus: () => void;
    handleBlur: (event: FocusEvent<HTMLInputElement>) => void;
    reset: () => void;
    dawaQuery: string;
    interactionContext: TypeaheadInteractionContext;
};

const useAddressSuggestionService = (params: UseAddressSuggestionServiceParams): UseAddressSuggestionServiceResult => {
    const adapter = useDawaAddressAdapter(params);

    return {
        options: adapter.options,
        matchOption: adapter.matchOption,
        isLoading: adapter.isLoading,
        status: adapter.status,
        kvhx: adapter.kvhx,
        kvhxUrl: adapter.kvhxUrl,
        selectedSuggestion: adapter.selectedSuggestion,
        selectedCustomValue: adapter.selectedCustomValue,
        selectedOptionValue: adapter.selectedOptionValue,
        validationStrategy: adapter.validationStrategy,
        dropdownCloseStrategy: adapter.dropdownCloseStrategy,
        shouldAutoOpen: adapter.shouldAutoOpen,
        shouldOpenOnFocus: adapter.shouldOpenOnFocus,
        handleInputValueChange: adapter.handleInputChange,
        handleHiddenInputChange: adapter.handleHiddenInputChange,
        handleFocus: adapter.handleFocus,
        handleBlur: adapter.handleBlur,
        reset: adapter.reset,
        dawaQuery: adapter.dawaQuery,
        interactionContext: adapter.interactionContext,
    };
};

export type { UseAddressSuggestionServiceParams, UseAddressSuggestionServiceResult };
export { useAddressSuggestionService };
